// Room_ModUI.tsx — Buildings Set1 §8 editor for `Mod_Room`. Surfaces `shapeSource` (kind dropdown
// + index/path-id) plus the room's inheritable fields via `InheritableField`. P2 adds the
// footprint resolution readout (what auto resolved to).
import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Row, Select, Spinner, Text} from "react-vcomponents";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_Room} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Room.js";
import {BuildingShapeSourceKind, ShapeSourceRef} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Building/BuildingShared.js";
import {ResolveBuildingDefaults, ResolveRoomParams} from "../../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {ResolveRoomFootprint_Local} from "../../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {InheritableField} from "./Building/InheritableField.js";

export const Room_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, obj} = props;
	const Change = (mutator: (data: Mod_Room)=>any)=>ChangeModWrapper(a=>mutator(a.room!));
	const data = mod.room!;

	const resolved = ResolveRoomParams(obj);
	const resolvedDefaults = ResolveBuildingDefaults(obj);
	const prov = (path: string)=>resolvedDefaults.provenance[path];

	const shape = data.shapeSource;
	const shapeKind = shape?.kind ?? null;
	const footprint = ResolveRoomFootprint_Local(obj);
	const footprintSummary = footprint ? `${footprint.length} points` : null;

	return (
		<>
			<Text style={{color: "#999", fontSize: 12}}>A single space within a Level's subtree. P2: footprint resolved + 2D plan overlays.</Text>

			<Row mt={5}>
				<Text style={{color: "#ccc"}}>Shape source:</Text>
				<Select ml={5} displayType="button bar"
					options={[{name: "Auto", value: null}, ...GetEntries(BuildingShapeSourceKind, "ui")]}
					value={shapeKind}
					onChange={val=>Change(a=>a.shapeSource = val == null ? null : new ShapeSourceRef({kind: val, index: 0}))}/>
			</Row>
			{shape != null && shape.kind != BuildingShapeSourceKind.shape &&
				<Row mt={5}>
					<Text style={{color: "#ccc"}}>Index:</Text>
					<Spinner ml={5} step={1} min={0} value={shape.index ?? 0}
						onChange={val=>Change(a=>{ a.shapeSource!.index = Math.max(0, Math.floor(val)); })}/>
				</Row>
			}
			{/* P2 — what the footprint actually resolved to. */}
			{footprint ? (
				<Text style={{color: "#999", fontSize: 11}}>Footprint: {footprintSummary} (source: {shapeKind ?? "auto"}).</Text>
			) : (
				<Text style={{color: "#f88", fontSize: 11}}>⚠ No resolvable footprint — add a basic shape, a mesh, or a closed path.</Text>
			)}

			{(()=>{
				const f = (key: keyof Mod_Room, label: string, number: {min?: number; max?: number; step?: number})=>{
					// floorThickness/wallHeight live in the "level" slice of the resolved defaults.
					const slice = key == "floorThickness" || key == "wallHeight" ? "level" : "room";
					const resolvedValue = (key == "floorThickness" || key == "wallHeight"
						? ResolveBuildingDefaults(obj).values.level : resolved)[key] as number;
					const provenance = prov(`${slice}.${key}`);
					return <InheritableField key={key} label={label} info="Inherited via the building fallback chain."
						resolvedValue={resolvedValue} value={data[key] ?? null}
						provenance={provenance}
						onChange={(v)=>Change(a=>{ (a as any)[key] = v as number|n; })}
						number={number}/>;
				};
				return <>
					{f("wallThickness", "Wall thickness (m)", {min: 0.05, max: 2, step: 0.05})}
					{f("floorThickness", "Floor thickness (m)", {min: 0.05, max: 2, step: 0.05})}
					{f("wallHeight", "Wall height (m)", {min: 1.5, max: 20, step: 0.1})}
					<InheritableField label="Floor" info="Whether this room has a floor slab." text={false}
						resolvedValue={resolved.hasFloor} value={data.hasFloor ?? null} provenance={prov("room.hasFloor")}
						onChange={v=>Change(a=>a.hasFloor = v == null ? null : !!v)}/>
					<InheritableField label="Ceiling" info="Whether this room has a ceiling slab." text={false}
						resolvedValue={resolved.hasCeiling} value={data.hasCeiling ?? null} provenance={prov("room.hasCeiling")}
						onChange={v=>Change(a=>a.hasCeiling = v == null ? null : !!v)}/>
				</>;
			})()}

			<Text style={{color: "#999", fontSize: 11, marginTop: 4}}>Roof contribution + materials slots land in P5/P6.</Text>
		</>
	);
});
